/**
 * Appearance tab for UserSettingsModal — theme and UI preferences.
 */

import React from 'react';
import { useThemeStore } from '@/shared/store/themeStore';
import type { ThemePreference } from '@/shared/utils/themeBoot';

interface AppearanceTabProps {
  headerPinned: boolean;
  onHeaderPinnedChange: (pinned: boolean) => void;
  onThemeChange: (theme: ThemePreference) => void;
}

const THEME_OPTIONS: ReadonlyArray<{
  value: ThemePreference;
  label: string;
  icon: string;
  hint: string;
}> = [
  { value: 'light', label: 'Light', icon: 'fa-sun', hint: 'Always light' },
  { value: 'dark', label: 'Dark', icon: 'fa-moon', hint: 'Always dark' },
  { value: 'system', label: 'System', icon: 'fa-circle-half-stroke', hint: 'Match your device' },
];

export const AppearanceTab: React.FC<AppearanceTabProps> = ({
  headerPinned,
  onHeaderPinnedChange,
  onThemeChange,
}) => {
  const preference = useThemeStore((state) => state.preference);
  const resolved = useThemeStore((state) => state.resolved);

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-bold text-ink mb-2">Appearance</h3>
        <p className="text-ink-muted text-sm">Customize how the app looks and behaves.</p>
      </div>

      <div className="bg-surface border border-line rounded-lg p-4">
        <div className="mb-3">
          <p className="font-medium text-ink">Theme</p>
          <p className="text-sm text-ink-muted">
            {preference === 'system'
              ? `Following your device, currently ${resolved}`
              : 'Applies on every device you sign in to'}
          </p>
        </div>

        <div role="radiogroup" aria-label="Theme" className="grid grid-cols-3 gap-2">
          {THEME_OPTIONS.map((option) => {
            const isSelected = preference === option.value;
            return (
              <button
                key={option.value}
                type="button"
                role="radio"
                aria-checked={isSelected}
                onClick={() => onThemeChange(option.value)}
                className={`flex flex-col items-center gap-1 rounded-lg border px-3 py-3 transition-colors focus:outline-none focus:ring-2 focus:ring-focus focus:ring-offset-2 ${
                  isSelected
                    ? 'border-selected-border-strong bg-selected-subtle text-selected-ink'
                    : 'border-line bg-surface text-ink-body hover:bg-surface-hover'
                }`}
              >
                <i className={`fa-solid ${option.icon} text-lg`} aria-hidden="true" />
                <span className="text-sm font-medium">{option.label}</span>
                <span className="text-xs text-ink-muted">{option.hint}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="bg-surface border border-line rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium text-ink">Pin Header</p>
            <p className="text-sm text-ink-muted">
              Keep the header visible at the top when scrolling
            </p>
          </div>
          <button
            onClick={() => onHeaderPinnedChange(!headerPinned)}
            className={`relative w-12 h-7 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-focus focus:ring-offset-2 ${
              headerPinned ? 'bg-selected' : 'bg-surface-deep'
            }`}
          >
            <span className={`absolute top-0.5 left-0.5 w-6 h-6 bg-surface rounded-full shadow-md transition-transform ${
              headerPinned ? 'translate-x-5' : 'translate-x-0'
            }`} />
          </button>
        </div>
      </div>
    </div>
  );
};
